import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import SEO from '../../components/SEO';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccess(false);
    
    // Validate email
    if (!email?.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `https://aduconstr7921.builtwithrocket.new/reset-password`,
      });
      
      if (resetError) {
        throw resetError;
      }
      
      setSuccess(true);
      setEmail('');
    } catch (err) {
      console.error('Password reset error:', err);
      
      // Handle specific error cases
      if (err?.message?.includes('rate limit')) {
        setError('Too many requests. Please try again later.');
      } else if (err?.message?.includes('Invalid email')) {
        setError('Please enter a valid email address.');
      } else {
        // Don't reveal if email exists or not for security
        setSuccess(true);
        setEmail('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
      <SEO
        title="Forgot Password — ADU Flow Account Recovery"
        description="Reset your ADU Flow account password. Enter your email address and we'll send you a link to create a new password."
        canonical="/forgot-password"
        keywords="forgot password, password reset, account recovery, ADU Flow"
        noIndex={true}
      />
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Icon name="Home" size={32} color="#FFFFFF" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Forgot Password</h1>
          <p className="text-muted-foreground">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-md flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Check your email</p>
                <p className="text-sm text-muted-foreground">
                  If an account exists with that email, we've sent password reset instructions. Please check your inbox and spam folder.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e?.target?.value);
                setError('');
                setSuccess(false);
              }}
              error={error && !email?.trim() ? error : ''}
              required
              autoComplete="email"
              disabled={loading}
              autoFocus
            />

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              className="mt-6"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          {/* Back to Login Link */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-accent hover:text-accent/80 font-medium transition-colors inline-flex items-center gap-2"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-accent hover:text-accent/80 font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;